/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\RecordingFileUploadSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Recording Management module.
----------------------------------------------------------------*/


import { Upload, X, FileAudio, Play, Download } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import type { VoiceRecording } from '../types';

interface SingleFileUpload {
    selectedFile: File | null;
    filePreview: string | null;
    dragActive: boolean;
    fileInputRef: React.RefObject<HTMLInputElement>;
    handleDrag: (e: React.DragEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    removeFile: () => void;
}

interface RecordingData {
    isEditMode: boolean;
    audioUrl: string | null;
    recording: VoiceRecording | null;
}

interface RecordingFileUploadSectionProps {
    fileUpload: SingleFileUpload;
    data: RecordingData;
    onPlayError: () => void;
    t: (key: string, fallback?: string) => string;
}

export function RecordingFileUploadSection({ fileUpload, data, onPlayError, t }: RecordingFileUploadSectionProps) {
    const showDropZone = !fileUpload.selectedFile && (!data.isEditMode || !data.recording || !data.recording?.filename);

    return (
        <FormBox title={t('recordingManagement.sections.fileUpload')}>
            <div className="space-y-4">
                {showDropZone ? (
                    <div
                        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${fileUpload.dragActive
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                            }`}
                        onDragEnter={fileUpload.handleDrag}
                        onDragLeave={fileUpload.handleDrag}
                        onDragOver={fileUpload.handleDrag}
                        onDrop={fileUpload.handleDrop}
                    >
                        <FileAudio className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-lg font-medium mb-2">{t('recordingManagement.upload.dragDrop')}</p>
                        <p className="text-sm text-muted-foreground mb-4">{t('recordingManagement.upload.supportedFormats')}</p>
                        <Button type="button" variant="outline" onClick={() => fileUpload.fileInputRef.current?.click()}>
                            <Upload className="h-4 w-4 mr-2" />
                            {t('recordingManagement.upload.selectFile')}
                        </Button>
                        <input
                            ref={fileUpload.fileInputRef}
                            type="file"
                            accept="audio/*"
                            onChange={fileUpload.handleFileInputChange}
                            className="hidden"
                        />
                    </div>
                ) : (
                    <div className="border rounded-lg p-4 bg-muted/50">
                        <div className="flex items-center justify-between gap-4">
                            {/* File info */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                <FileAudio className="h-8 w-8 text-primary flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">
                                        {fileUpload.selectedFile
                                            ? fileUpload.selectedFile.name
                                            : (data.recording?.filename || t('recordingManagement.upload.existingFile', 'Existing File'))}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        {fileUpload.selectedFile
                                            ? `${(fileUpload.selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                                            : (data.recording?.file_size
                                                ? `${(data.recording.file_size / 1024 / 1024).toFixed(2)} MB`
                                                : '')}
                                    </p>
                                </div>
                            </div>

                            {/* Audio player */}
                            {(fileUpload.filePreview || (data.isEditMode && data.recording && !data.recording.is_ai_generated && data.audioUrl)) && (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <audio
                                        ref={(el) => { if (el) (window as any).__uploadAudioRef = el; }}
                                        src={fileUpload.filePreview || data.audioUrl || ''}
                                        className="hidden"
                                        key={fileUpload.filePreview || data.audioUrl || ''}
                                        onError={onPlayError}
                                    />
                                    <Button
                                        type="button" variant="ghost" size="sm"
                                        onClick={() => { const a = (window as any).__uploadAudioRef as HTMLAudioElement; if (a) a.play(); }}
                                        className="h-8 w-8 p-0 text-green-600 hover:bg-green-500/10 hover:text-green-600"
                                        title={t('recordingManagement.actions.play', 'Play')}
                                    >
                                        <Play className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        type="button" variant="ghost" size="sm"
                                        onClick={() => {
                                            const link = document.createElement('a');
                                            link.href = fileUpload.filePreview || data.audioUrl || '';
                                            link.download = fileUpload.selectedFile?.name || data.recording?.filename || 'recording.wav';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                        className="h-8 w-8 p-0 text-cyan-600 hover:bg-cyan-500/10 hover:text-cyan-600"
                                        title={t('recordingManagement.actions.download', 'Download')}
                                    >
                                        <Download className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}

                            {/* Action buttons */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                                {!fileUpload.selectedFile && data.isEditMode && data.recording && (
                                    <Button type="button" variant="outline" size="sm" onClick={() => fileUpload.fileInputRef.current?.click()}>
                                        <Upload className="h-4 w-4 mr-2" />
                                        {t('recordingManagement.upload.replace')}
                                    </Button>
                                )}
                                <Button type="button" variant="outline" size="sm" onClick={fileUpload.removeFile}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        <input
                            ref={fileUpload.fileInputRef}
                            type="file"
                            accept="audio/*"
                            onChange={fileUpload.handleFileInputChange}
                            className="hidden"
                        />
                    </div>
                )}
            </div>
        </FormBox>
    );
}
